import { z } from 'zod';

/**
 * Audience: who can see a record.
 *  - private:  only the owner
 *  - selected: the owner plus specific family members
 *  - adults:   every adult (manager or adult) in the family
 *  - family:   everyone in the family, including children
 */
export const AUDIENCES = ['private', 'selected', 'adults', 'family'] as const;
export type Audience = (typeof AUDIENCES)[number];
export const audienceSchema = z.enum(AUDIENCES);

/**
 * Family roles. Managers are adults who can invite/remove members and change
 * family settings. Every family must keep at least one manager.
 */
export const ROLES = ['manager', 'adult', 'child'] as const;
export type Role = (typeof ROLES)[number];
export const roleSchema = z.enum(ROLES);

export const isAdultRole = (role: Role): boolean => role !== 'child';

/** Fields every synchronized record carries. Maintained by the server. */
export interface SystemFields {
  id: string;
  familyId: string;
  /**
   * Whose audience settings govern this record. For top-level records this is
   * the creator (or null once the creator has left the family, in which case
   * managers act as owner). Child records mirror their parent's owner.
   */
  ownerId: string | null;
  audience: Audience;
  /** Member ids that can see the record when audience is "selected". */
  sharedWith: string[];
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Monotonic per-family revision assigned by the server on every change. */
  rev: number;
}

/** System fields a client may set in a mutation (subject to permission checks). */
export const CLIENT_WRITABLE_SYSTEM_FIELDS = ['audience', 'sharedWith'] as const;

export type AccessFields = Pick<SystemFields, 'ownerId' | 'audience' | 'sharedWith' | 'createdBy'>;
